// @givmo/org-console — the PATH-MODULE SUBSTRATE: what it takes for a module to be MOUNTED inside a
// console, decided ONCE for every host (OM-12).
//
// ══ THE FOUR QUESTIONS A PATH-MOUNTED MODULE ASKS ══════════════════════════════════════════════
//
// A module used to be its own deployed host, reached from the app grid by an absolute URL. A module
// is now a PATH inside the console that serves its organization — and that turns four things nobody
// had to answer into things everybody has to answer the same way:
//
//   1. WHICH MODULE AM I IN? — read off the address bar, never declared by the page. {@link
//      currentModuleFor}
//   2. WHERE DOES A TILE GO? — the module's own path when this build serves it, the configured URL
//      verbatim when it does not. {@link moduleHref}
//   3. MAY I BE HERE? — the tile's five-state answer, applied to a whole route tree for someone who
//      arrived on a direct link instead of through the grid. `./module-gate.tsx`
//   4. WHICH DOOR DID I ARRIVE THROUGH? — one per-module sign-in, addressed by PATH SEGMENT.
//      {@link moduleSegment}
//
// (What chrome a module page wears is no longer a question: shell contract v3 gives every page ONE
// layout, the console's sidebar with the module's own rows — `agent_docs/org-console-shell.md`, T6.1.)
//
// Three module plans and a second class console all need these at once, so they are written here
// rather than in whichever one lands first — three answers to one question is the drift the shared
// verdict rule already exists to stop, one level up.
//
// ══ PURE OVER A HOST-SUPPLIED LIST ═════════════════════════════════════════════════════════════
//
// Nothing here reads a registry, a router or an environment. A host hands in {@link ModuleRoute}[]
// plus the current pathname and origin, and gets an answer back. That is what lets a host WITHOUT a
// module registry — one whose module list is the organization's own catalog — use exactly these
// mechanics rather than growing a second set beside them.
//
// ══ MOUNTED IN THIS BUILD ══════════════════════════════════════════════════════════════════════
//
// The load-bearing definition, and it has TWO halves because either alone is a lie:
//
//   a registry `path` (the module has an address here at all)
//   AND a configured destination (this build was told where that module lives)
//
// The second half is the structural reveal gate the whole grid rests on: a module renders only where
// its URL variable is SET, so an unconfigured environment cannot produce a control that goes nowhere.
// A module mounted in-app is NOT exempt from it — its variable simply names this console's own origin
// plus the module's path, which is exactly how the first mounts are configured. Dropping the second
// half would give every environment a tile with no operator switch behind it.

/**
 * One module's address, as the HOST knows it.
 *
 * A host with a registry derives this from the registry plus its build variables; a host whose module
 * list is the organization's own catalog derives it from that. Either way the package sees only this.
 */
export interface ModuleRoute {
  /** The module's slug — the backend's product vocabulary, and the tile's testid. */
  slug: string;
  /**
   * The in-console path this module is served at, when a build has given it one.
   *
   * ABSENT IS THE HONEST ENTRY for a module whose address is not yet decided; inventing one is how a
   * mount and a sign-in route end up disagreeing about their own addresses.
   */
  path?: string | undefined;
  /**
   * This build's configured destination for the module — the URL variable's value, read verbatim.
   *
   * Undefined means the variable is UNSET, which means the module is not built here. There is no
   * default and there must never be one: a guessed origin looks like a working link and lands
   * somewhere that may not exist in this environment.
   */
  href?: string | undefined;
}

/** Where a module's tile or square actually goes. */
export type ModuleDestination =
  | { kind: 'in-app'; href: string }
  | {
      kind: 'external';
      href: string;
      /**
       * The configured URL is on THIS origin but names a different path than the module's.
       *
       * A configuration defect rather than a routing decision, so it is carried out as a VALUE the
       * host can report — the destination itself stays the configured URL, verbatim. Guessing the
       * module's path instead would be the one thing this whole file refuses: a link this build
       * invented rather than one an operator set.
       */
      pathMismatch?: true;
    };

/**
 * The module's path, normalized — or `undefined` when it has none that could match a URL.
 *
 * A trailing slash is normalized away (an operator writing `/books/` is not making a mistake), and
 * the root is deliberately NOT a module path: `/` is the console itself, and a "module" claiming it
 * would swallow every page in the build.
 */
function modulePath(route: ModuleRoute): string | undefined {
  const raw = (route.path ?? '').trim();
  if (!raw.startsWith('/')) return undefined;
  const trimmed = raw.replace(/\/+$/, '');
  return trimmed.length > 1 ? trimmed : undefined;
}

/** The configured destination, trimmed — or `undefined` when the variable is unset. */
function configuredHref(route: ModuleRoute): string | undefined {
  const raw = (route.href ?? '').trim();
  return raw.length > 0 ? raw : undefined;
}

/**
 * Whether THIS build has the module mounted: it has a path here AND a configured destination.
 *
 * See the header for why both halves are required. This is the predicate behind the sign-in
 * vocabulary, the layout mode and the gate's not-found answer, so all three agree by construction.
 */
export function isMountedInThisBuild(route: ModuleRoute): boolean {
  return modulePath(route) !== undefined && configuredHref(route) !== undefined;
}

/** Whether `pathname` is the path itself or sits underneath it. */
function isUnder(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * Which module a pathname is inside, by SEGMENT-BOUNDARY prefix, longest match first.
 *
 * ⚠ SEGMENT BOUNDARY, NOT `startsWith`. `/bookshelf` starts with `/books` as a string and is not
 * inside it as a route; a bare prefix test would hand a whole unrelated page to another module's
 * chrome and gate. The boundary is the separator, so `/books` and `/books/anything` match and
 * `/bookshelf` does not.
 *
 * ⚠ LONGEST MATCH, NOT FIRST MATCH — the same rule the sidebar and the breadcrumb already resolve
 * with, and for the same reason: a short path is a prefix of the longer ones nested under it, so
 * first-match answers by declaration order rather than by what the address bar says.
 *
 * The host decides WHICH routes to hand in. Passing only the routes this build actually mounted is
 * the ordinary call, and it is what makes an unmounted path answer "no module" rather than dressing
 * a not-found page in a module's chrome.
 */
export function currentModuleFor(
  pathname: string,
  routes: readonly ModuleRoute[],
): string | undefined {
  let bestSlug: string | undefined;
  let bestLength = 0;
  for (const route of routes) {
    const path = modulePath(route);
    if (path === undefined || !isUnder(pathname, path)) continue;
    if (bestSlug === undefined || path.length > bestLength) {
      bestSlug = route.slug;
      bestLength = path.length;
    }
  }
  return bestSlug;
}

/** The one route matching a slug, if the host declared it. */
export function moduleRouteFor(
  slug: string,
  routes: readonly ModuleRoute[],
): ModuleRoute | undefined {
  return routes.find((route) => route.slug === slug);
}

/**
 * The single path SEGMENT a module's mount owns (`/books` → `books`) — the vocabulary a per-module
 * route parameter is validated against.
 *
 * ⚠ THE SEGMENT IS NOT THE SLUG, deliberately: the slug is the backend's product vocabulary and the
 * segment is what a person reads in the address bar, and the two differ today (a module whose slug
 * names an audience can sit at a path naming the work). A route parameter matches exactly one
 * segment, so a multi-segment path has none to give and answers `undefined` rather than a value that
 * could never match.
 */
export function moduleSegment(route: ModuleRoute): string | undefined {
  const path = modulePath(route);
  if (path === undefined) return undefined;
  const segment = path.slice(1);
  return segment.includes('/') ? undefined : segment;
}

/** The mounted module owning a path segment — the per-module door's whole vocabulary check. */
export function moduleForSegment(
  segment: string,
  routes: readonly ModuleRoute[],
): ModuleRoute | undefined {
  const wanted = segment.trim();
  if (wanted.length === 0) return undefined;
  return routes.find((route) => isMountedInThisBuild(route) && moduleSegment(route) === wanted);
}

/** Parse an absolute URL, or `null` for anything that is not one. */
function absoluteUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/** An origin string reduced to a comparable origin, or the trimmed input when it is not a URL. */
function normalizeOrigin(origin: string): string {
  const trimmed = origin.trim();
  return absoluteUrl(trimmed)?.origin ?? trimmed;
}

/**
 * Where a module's square, tile or landing hop actually goes — the ONE resolver every consumer uses.
 *
 * `null` means the module is not mounted in this build (see {@link isMountedInThisBuild}); there is
 * nothing to link to, and the caller renders no control rather than one going nowhere.
 *
 * ⚠ SAME-ORIGIN IS DECIDED AGAINST THE LIVE ORIGIN, AT CALL TIME. The same bundle is deployed to
 * several environments under different hostnames, so "is this module served by the build I am
 * running in" is not answerable when the bundle is compiled — only when it runs. The caller passes
 * `window.location.origin`.
 *
 * ⚠ AND THE CONFIGURED PATH MUST BE THE MODULE'S OWN. An operator sets the variable to this origin
 * plus the module's path; if the two disagree, the configuration is wrong and this refuses to paper
 * over it — the answer is the configured URL verbatim, flagged, never a path this code guessed.
 */
export function moduleHref(route: ModuleRoute, origin: string): ModuleDestination | null {
  const path = modulePath(route);
  const configured = configuredHref(route);
  if (path === undefined || configured === undefined) return null;

  const url = absoluteUrl(configured);
  // Not an absolute URL at all: nothing to compare an origin against, so it stays what it is.
  if (url === null || url.origin !== normalizeOrigin(origin)) {
    return { kind: 'external', href: configured };
  }
  const configuredPath = url.pathname.replace(/\/+$/, '');
  return configuredPath === path
    ? { kind: 'in-app', href: path }
    : { kind: 'external', href: configured, pathMismatch: true };
}
